import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { subHours } from 'date-fns';
import { LngLatBounds } from 'maplibre-gl';

import { AlertHistoryResponse } from '@entities/alert';
import {
  ShipmentAlertHistoryResponse,
  ShipmentSensorAlertHistoryResponse,
} from '@entities/shipment';
import { DashboardService } from '@services/api/dashboard.service';
import { LoadingService } from '@services/loading.service';
import { CustomValidator } from '@utils/custom-validators';
import {
  AlertDetailComponent,
  AlertDetailModel,
  SensingHistoryModel,
} from './alert-detail/alert-detail.component';
import {
  AlertMapComponent,
  SensingLocationViewModel,
} from './alert-map/alert-map.component';
import {
  AlertSummaryComponent,
  SensingListViewModel,
  SensingType,
} from './alert-summary/alert-summary.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ShipmentService } from '@services/api/shipment.service';
import {
  ShipmentSensorAlertHistoryViewModel,
  ShipmentSensorAlertDetailComponent,
  ShipmentSensorAlertModel,
} from './shipment-sensor-alert-detail/shipment-sensor-alert-detail.component';
import {
  ShipmentAlertDetailComponent,
  ShipmentAlertHistoryModel,
} from './shipment-alert-detail/shipment-alert-detail.component';
import { ShipmentAlertMapComponent } from './shipment-alert-map/shipment-alert-map.component';
import { finalize } from 'rxjs';
import { AccountResponse } from '@entities/account';
import { ActivatedRoute } from '@angular/router';

/** センターのステータス */
enum SensingStatus {
  Alert = 'Alert',
  PreAlert = 'PreAlert',
}

/** ユーザー種別 */
export type PageViewModel = 'device' | 'shipment';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    AlertMapComponent,
    AlertDetailComponent,
    AlertSummaryComponent,
    ShipmentAlertDetailComponent,
    ShipmentSensorAlertDetailComponent,
    ShipmentAlertMapComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  /** サマリ表示情報 */
  public latestDevicesSummary: SensingListViewModel = {};

  /** アラート履歴 */
  private alertList: AlertHistoryResponse = {};

  /** アラート一覧 */
  public alertDetailList: SensingHistoryModel[] = [];

  /** アラート位置情報一覧 */
  public alertLocationList: SensingLocationViewModel[] = [];

  /** 選択された計測種別 */
  public selectedSensingType: SensingType = 'temperature';

  /** 選択された計測種別の値の単位 */
  public selectedSensingUnit: string = '';

  /** マップ表示範囲 */
  public mapBounds: LngLatBounds | undefined;

  /** 選択されたアラート詳細 */
  public selectedAlertDetail: AlertDetailModel | undefined;

  /** 選択されたマーカー */
  public selectedMarker: SensingLocationViewModel | undefined;

  /** デバイスタブの検索フォーム */
  public deviceHistoryFormGroup: FormGroup;

  /** 配送タブの検索フォーム */
  public shipmentHistoryFormGroup: FormGroup;

  /** アラート詳細をローディング中か */
  public isMapMoving = false;

  /** アラート履歴を取得中か */
  public alertLoading = false;

  /** アラート履歴を処理中か */
  public alertProcessing = false;

  /** 配送アラートを取得中か */
  public shipmentAlertLoading = false;

  /** 配送アラートを処理中か */
  public shipmentAlertProcessing = false;

  /** 配送センサーアラートを取得中か */
  public shipmentSensorAlertLoading = false;

  /** 配送センサーアラートを処理中か */
  public shipmentSensorAlertProcessing = false;

  /** 選択された表示画面 */
  public selectedDisplayPage: PageViewModel = 'device';

  /** 配送センサーアラート履歴 */
  public shipmentSensorAlertList: ShipmentSensorAlertHistoryViewModel[] = [];

  /** 配送アラート履歴 */
  public shipmentSensorAlertLocationList: ShipmentSensorAlertModel[] = [];

  /** 選択されたアラート詳細 */
  public selectedSensorAlertDetail: ShipmentSensorAlertModel | undefined;

  /** 選択されたアラートマーカー */
  public selectedSensorAlertMarker: ShipmentSensorAlertModel | undefined;

  /** 配送アラート履歴 */
  public shipmentAlertList: ShipmentAlertHistoryModel[] = [];

  /** テナント作成日時 */
  public tenantCreatedAt: string | null = '';

  constructor(
    private dashboardService: DashboardService,
    private shipmentService: ShipmentService,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
  ) {
    const tenant = route.snapshot.data['loggedInUser'].tenant;
    this.tenantCreatedAt = tenant.createdAt;
    const now = new Date();
    this.deviceHistoryFormGroup = new FormGroup(
      {
        historyFrom: new FormControl(subHours(now, 1), {
          validators: [
            Validators.required,
            CustomValidator.earlyDateValidator(2),
            CustomValidator.NotBeforeCreationDateValidator(
              this.tenantCreatedAt,
            ),
          ],
        }),
        historyTo: new FormControl(now, {
          validators: [Validators.required],
        }),
      },
      {
        validators: CustomValidator.startDateTimeBeforeEndDatetimeValidator(
          'historyFrom',
          'historyTo',
        ),
      },
    );
    this.shipmentHistoryFormGroup = new FormGroup(
      {
        historyFrom: new FormControl(subHours(now, 12), {
          validators: [Validators.required],
        }),
        historyTo: new FormControl(now, {
          validators: [Validators.required],
        }),
      },
      {
        validators: CustomValidator.startDateTimeBeforeEndDatetimeValidator(
          'historyFrom',
          'historyTo',
        ),
      },
    );
  }

  ngOnInit() {
    this.findAlertList();

    // マクニカ管理者であれば、2ヵ月以上前でも指定可能とする
    const loggedInUser: AccountResponse =
      this.route.snapshot.data['loggedInUser'];
    if (loggedInUser.tenant.roleId == 'system_admin') {
      this.deviceHistoryFormGroup.controls['historyFrom'].setValidators([
        Validators.required,
        CustomValidator.NotBeforeCreationDateValidator(this.tenantCreatedAt),
      ]);
    }
  }

  public findAlertList() {
    if (this.selectedDisplayPage === 'device') {
      this.findHistoryList();
    } else {
      this.findShipmentAlertList();
    }
  }

  /**
   * 選択されたセンサータイプが変更された時
   * @param sensorType センサータイプ
   */
  public onSensingTypeChanged(sensorType: SensingType) {
    this.selectedSensingType = sensorType;
    this.selectedAlertDetail = undefined;
    this.selectedMarker = undefined;
    this.setAlertList();
  }

  /**
   * アラート履歴を取得する
   */
  private findHistoryList() {
    // API呼び出し時のローディング表示とは別に、地図表示とアラート表示が完了するまでローティング表示を行う
    this.loadingService.show();
    this.alertLoading = true;

    const historyFrom =
      this.deviceHistoryFormGroup.value.historyFrom.toISOString();
    const historyTo = this.deviceHistoryFormGroup.value.historyTo.toISOString();

    this.dashboardService
      .findHistoryList(historyFrom, historyTo)
      .pipe(
        finalize(() => {
          this.alertLoading = false;
          this.hideAppLoading();
        }),
      )
      .subscribe({
        next: (histories) => {
          this.alertProcessing = true;
          this.alertList = histories;
          this.latestDevicesSummary = this.aggregateHistorySummary(
            JSON.parse(JSON.stringify(histories)),
          );
          this.setAlertList();

          setTimeout(() => {
            this.alertProcessing = false;
            this.hideAppLoading();
          });
        },
      });
  }

  /**
   * 配送のアラート履歴を取得する
   */
  private findShipmentAlertList() {
    // API呼び出し時のローディング表示とは別に、地図表示とアラート表示が完了するまでローティング表示を行う
    this.loadingService.show();
    this.shipmentSensorAlertLoading = true;
    this.shipmentAlertLoading = true;

    const historyFrom =
      this.shipmentHistoryFormGroup.value.historyFrom.toISOString();
    const historyTo =
      this.shipmentHistoryFormGroup.value.historyTo.toISOString();

    this.shipmentService
      .findSensorAlertListByRange(historyFrom, historyTo)
      .pipe(
        finalize(() => {
          this.shipmentSensorAlertLoading = false;
          this.hideAppLoading();
        }),
      )
      .subscribe({
        next: (alertList: ShipmentSensorAlertHistoryResponse[]) => {
          this.shipmentSensorAlertProcessing = true;
          let latestEventAt: string | undefined;
          this.shipmentSensorAlertList = [];
          for (const alert of alertList) {
            const alertDetailList: ShipmentSensorAlertModel[] = [];
            const alertHistory = alert.alertHistory;

            const latestEventAtList = Object.entries(alertHistory).flatMap(
              ([, alerts]) =>
                alerts
                  .map((alert) => alert.latestEventAt)
                  .filter((date): date is string => date !== undefined),
            );

            // 最大の日付を取得
            latestEventAt =
              latestEventAtList.length > 0
                ? latestEventAtList.reduce((a, b) => (a > b ? a : b))
                : undefined;

            for (const [sensor, historyList] of Object.entries(alertHistory)) {
              for (const history of historyList) {
                for (const alertDetail of history.alertDetails || []) {
                  const shipmentAlertDetail: ShipmentSensorAlertModel = {
                    shipmentId: alert.shipmentId,
                    shipmentName: alert.shipmentName,
                    deviceId: history.deviceId,
                    deviceName: history.deviceName,
                    sensor: {
                      label: sensor,
                      unit: this.getUnit(sensor),
                    },
                    ...alertDetail,
                  };
                  alertDetailList.push(shipmentAlertDetail);
                }
              }
            }

            alertDetailList.sort((a, b) => {
              const dateA = new Date(a.eventAt || 0).getTime();
              const dateB = new Date(b.eventAt || 0).getTime();
              return dateB - dateA;
            });

            const shipmentSensorAlert: ShipmentSensorAlertHistoryViewModel = {
              shipmentId: alert.shipmentId,
              shipmentName: alert.shipmentName,
              startDatetime: alert.startDatetime,
              actualStartDatetime: alert.actualStartDatetime,
              arrivalDatetime: alert.arrivalDatetime,
              actualArrivalDatetime: alert.actualArrivalDatetime,
              alertDetails: alertDetailList,
              latestEventAt: latestEventAt,
            };

            this.shipmentSensorAlertList.push(shipmentSensorAlert);
          }
          this.setShipmentAlertList();

          setTimeout(() => {
            this.shipmentSensorAlertProcessing = false;
            this.hideAppLoading();
          });
        },
      });

    this.shipmentService
      .findAlertListByRange(historyFrom, historyTo)
      .pipe(
        finalize(() => {
          this.shipmentAlertLoading = false;
          this.hideAppLoading();
        }),
      )
      .subscribe({
        next: (alertList: ShipmentAlertHistoryResponse[]) => {
          this.shipmentAlertProcessing = true;
          this.shipmentAlertList = [];
          for (const shipment of alertList) {
            const alertHistory = shipment.alertHistory;
            alertHistory.sort((a, b) => (a.eventAt > b.eventAt ? -1 : 1));

            for (const alert of shipment.alertHistory) {
              const shipmentAlert = {
                shipmentId: shipment.shipmentId,
                shipmentName: shipment.shipmentName,
                startDatetime: shipment.startDatetime,
                arrivalDatetime: shipment.arrivalDatetime,
                status: alert.status,
                eventAt: alert.eventAt,
                resolvedAt: alert.resolvedAt,
                judgedReason: alert.judgedReason,
              };
              this.shipmentAlertList.push(shipmentAlert);
            }
          }

          setTimeout(() => {
            this.shipmentAlertProcessing = false;
            this.hideAppLoading();
          });
        },
      });
  }

  /**
   * 地図の移動が開始した時
   */
  public onMapMoveStart() {
    this.isMapMoving = true;
  }

  /**
   * 地図の移動が終了した時
   * @param bounds 地図表示範囲
   */
  public onMapMoveEnd(bounds: LngLatBounds) {
    this.mapBounds = bounds;
    this.isMapMoving = false;

    this.hideAppLoading();
  }

  /**
   * 選択されてたアラートが変更された時
   * @param alertDetail アラート
   */
  public onAlertDetailChanged(alertDetail: AlertDetailModel) {
    this.selectedAlertDetail = alertDetail;
  }

  /**
   * 選択されたマーカ－が変更された時
   * @param marker マーカー
   */
  public onMarkerChanged(marker: SensingLocationViewModel) {
    this.selectedMarker = marker;
  }

  /**
   * 選択されてたアラートが変更された時
   * @param alertDetail アラート
   */
  public onShipmentSensorAlertDetailChanged(
    alertDetail: ShipmentSensorAlertModel,
  ) {
    this.selectedSensorAlertDetail = alertDetail;
  }

  /**
   * 選択されたマーカ－が変更された時
   * @param marker マーカー
   */
  public onShipmentMarkerChanged(marker: ShipmentSensorAlertModel) {
    this.selectedSensorAlertMarker = marker;
  }

  /**
   * 選択されたタブが変更された時
   * @param tab タブ名
   */
  public onDisplayPageChanged(tab: PageViewModel) {
    this.isMapMoving = false;
    this.selectedDisplayPage = tab;
    this.findAlertList();
  }

  /**
   * 単位を取得する
   * @param alertType アラート
   * @returns 単位
   */
  public getUnit(alertType: string): string {
    switch (alertType) {
      case 'temperature':
        return '℃';
      case 'humidity':
        return '%';
      case 'pressure':
        return 'hPa';
      case 'light':
        return 'lx';
      case 'tilt':
        return '°';
      default:
        return '';
    }
  }

  private hideAppLoading() {
    // 各データ取得もマップ表示も完了していたらローディングを非表示にする
    if (
      !this.alertLoading &&
      !this.alertProcessing &&
      !this.shipmentAlertLoading &&
      !this.shipmentAlertProcessing &&
      !this.shipmentSensorAlertLoading &&
      !this.shipmentSensorAlertProcessing &&
      !this.isMapMoving
    ) {
      this.loadingService.hide();
    }
  }

  private setAlertList() {
    const alertList = this.alertList[this.selectedSensingType] || [];
    this.alertDetailList = alertList;
    this.selectedSensingUnit = this.getUnit(this.selectedSensingType);
    const alertLocationList = alertList.reduce(
      (acc, obj) => {
        const alertDetails: AlertDetailModel[] = obj.alertDetails ?? [];
        const updatedAlertDetails = alertDetails.map((alert) => ({
          ...alert,
          deviceId: obj.deviceId,
          deviceName: obj.deviceName,
        }));

        return acc.concat(updatedAlertDetails);
      },
      [] as (AlertDetailModel & { deviceId: string; deviceName: string })[],
    );
    this.alertLocationList = alertLocationList;
  }

  private setShipmentAlertList() {
    const alertList = this.shipmentSensorAlertList;

    const shipmentSensorAlertLocationList:
      | ShipmentSensorAlertModel[]
      | undefined = alertList.flatMap((alert) => {
      if (!alert.alertDetails) return [];
      return alert.alertDetails.map((value) => {
        return {
          ...value,
        };
      });
    });

    this.shipmentSensorAlertLocationList = shipmentSensorAlertLocationList;
  }

  private aggregateHistorySummary(
    alertHistory: AlertHistoryResponse,
  ): SensingListViewModel {
    const currentSummary: SensingListViewModel = {};

    for (const [sensingKey, sensingHistories] of Object.entries(alertHistory)) {
      const summaryKey = sensingKey as keyof SensingListViewModel;

      for (const sensingHistory of sensingHistories) {
        if (!sensingHistory.alertDetails) {
          continue;
        }

        for (const sensingValue of sensingHistory.alertDetails) {
          const currentStatus = currentSummary[summaryKey]?.status;
          let newStatus = sensingValue?.status;
          if (newStatus?.startsWith(SensingStatus.Alert)) {
            newStatus = SensingStatus.Alert;
          } else if (newStatus?.startsWith(SensingStatus.PreAlert)) {
            newStatus = SensingStatus.PreAlert;
          }
          sensingValue.status = newStatus;

          const isKeepStatus = this.compareMaxStatus(currentStatus, newStatus);

          if (!isKeepStatus || !currentSummary[summaryKey]) {
            currentSummary[summaryKey] = sensingValue;
          }
        }
      }
    }

    return currentSummary;
  }

  private compareMaxStatus(
    aStatus: string | undefined,
    bStatus: string | undefined,
  ) {
    // ・Aのステータスがアラートならば、Aのステータス方が優先なので、trueを返す。
    // ・Aのステータスがプリアラート、かつ、Bのステータスがアラートならば、Bのステータス方が優先なので、falseを返す。
    // ・Aのステータスがプリアラートかアラートでない、かつ、Bのステータスがプリアラートかアラートならば
    //   Bのステータス方が優先なので、falseを返す。
    if (aStatus === SensingStatus.Alert) {
      return true;
    } else if (
      aStatus === SensingStatus.PreAlert &&
      bStatus === SensingStatus.Alert
    ) {
      return false;
    } else if (
      aStatus !== SensingStatus.Alert &&
      aStatus !== SensingStatus.PreAlert &&
      (bStatus === SensingStatus.Alert || bStatus === SensingStatus.PreAlert)
    ) {
      return false;
    }

    return true;
  }
}
